// services/api.js - Frontend API Integration
import axios from "axios";

const API_URL = import.meta.env.VITE_API_URL || "https://dashboard.lono.co.in/api";

// Create axios instance with base URL
const api = axios.create({
  baseURL: API_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Add a request interceptor to include the token
api.interceptors.request.use(
  (config) => {
    // Retrieve the token using the key defined in AuthContext.jsx
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Banking Partner API
export const bankingPartnerAPI = {
  // Get all banking partners
  getAll: async () => {
    try {
      const response = await api.get("/banking-partners");
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Get banking partner by ID
  getById: async (id) => {
    try {
      const response = await api.get(`/banking-partners/${id}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Create new banking partner
  create: async (bankingPartnerData) => {
    try {
      const response = await api.post("/banking-partners", bankingPartnerData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Update banking partner
  // Update banking partner
  update: async (id, data) => {
    try {
      const response = await api.put(`/banking-partners/${id}`, data);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Delete banking partner
  delete: async (id) => {
    try {
      const response = await api.delete(`/banking-partners/${id}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },
  getApplicationsByBankId: async (bankId) => {
    try {
      const response = await api.get(
        `/banking-partners/${bankId}/applications`
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching bank applications:", error);
      throw error;
    }
  },

  // Upload document
  uploadDocument: async (file, documentType, bankingPartnerId) => {
    try {
      // Create FormData for file upload
      const formData = new FormData();
      formData.append("document", file);
      formData.append("documentType", documentType);
      formData.append("bankingPartnerId", bankingPartnerId);

      const response = await api.post(
        "/banking-partners/upload", // Use relative path with the 'api' instance
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Update loan status
  updateLoanStatus: async (bankingPartnerId, loanIndex, status) => {
    try {
      const response = await api.patch(
        `/banking-partners/${bankingPartnerId}/loan/${loanIndex}/status`,
        {
          status,
        }
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Update insurance status
  updateInsuranceStatus: async (bankingPartnerId, insuranceIndex, status) => {
    try {
      const response = await api.patch(
        `/banking-partners/${bankingPartnerId}/insurance/${insuranceIndex}/status`,
        {
          status,
        }
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Get representatives for a banking partner
  getRepresentatives: async (bankingPartnerId) => {
    try {
      const response = await api.get(
        `/banking-partners/representatives/${bankingPartnerId}`
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Get dashboard card stats for a banking partner
  getDashboardCardStats: async (bankingPartnerId) => {
    try {
      const response = await api.get(
        `/banking-partners/${bankingPartnerId}/dashboard-stats`
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Get dashboard analytics with trends
  getDashboardAnalytics: async (bankingPartnerId) => {
    try {
      const response = await api.get(
        `/banking-partners/${bankingPartnerId}/analytics`
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },
};

// Loan Type API
export const loanTypeAPI = {
  // Get all loan types
  getAll: async () => {
    try {
      const response = await api.get("/loan-types");
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Create new loan type
  create: async (loanTypeData) => {
    try {
      const response = await api.post("/loan-types", loanTypeData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },
};
